/**
 * /dashboard/settings/audit-log — Settings → Organization → Audit log
 * (SOP §8). Embeds the tenant-scoped audit-log table at
 * /dashboard/audit-logs — same backend endpoint, same component.
 * HR-admin role gate is the UX mirror of GET /api/v1/audit-logs server
 * RBAC.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port
 * of frontend/src/app/(dashboard)/dashboard/settings/audit-log/page.tsx.
 */

import { createFileRoute } from "@tanstack/react-router";
import { ShieldX } from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { auditLogsApi } from "@/lib/api";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { AuditLogTable } from "@/components/superadmin/audit-log-table";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/audit-log",
)({
  component: SettingsAuditLogRoute,
});

const CAN_VIEW_AUDIT_LOGS = new Set<Role>([
  "company_admin",
  "hr_manager",
]);

function SettingsAuditLogRoute() {
  const { user } = useAuth();
  const allowed = !!user?.role && CAN_VIEW_AUDIT_LOGS.has(user.role);

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        {!allowed ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                <ShieldX className="h-6 w-6" />
              </span>
              <p className="text-base font-medium text-gray-900">
                Audit log access is restricted to HR admins
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Audit log</h2>
              <p className="text-sm text-muted-foreground">
                Who did what, when — for your company.
              </p>
            </div>
            <AuditLogTable
              fetcher={auditLogsApi.listAuditLogs}
              scope="tenant"
            />
          </div>
        )}
      </SettingsShell>
    </>
  );
}
